const CartQuantity = {
  MIN: 1,
  MAX: 99,
  DEBOUNCE_MS: 250,

  // The drawer and the cart page render the same stepper markup, so the read,
  // clamp, echo and coalescing must be one implementation. When they were copies the
  // cart page had already lost the local echo, and a burst across two lines read
  // stale values and landed on the wrong final quantity.
  //
  // The echo is the entry point to the line-local prediction in cart-optimistic.js,
  // which owns every value the click paints. This file keeps the read, the clamp and
  // the coalescing; it decides *when* a line repaints, not what.
  //
  // One window for every line, not one per line: a burst anywhere in the cart must
  // issue a single request once the clicking stops. Per-line timers let any gap
  // longer than the window flush mid-burst, which is what put a spinner on the very
  // control being clicked.

  // Per-host state, because the drawer ships in theme.liquid and so is live
  // alongside cart-page on /cart — one shared window could not tell them apart.
  createState() {
    return { pending: new Map(), rows: new Map(), timer: null };
  },

  handleStepperClick(event, { state, onFlush, onMax = null }) {
    const minusBtn = event.target.closest('[data-quantity-minus]');
    const plusBtn = event.target.closest('[data-quantity-plus]');
    if (!minusBtn && !plusBtn) return;

    const control = minusBtn ?? plusBtn;
    const row = control.closest('[data-item-key]');
    if (!row) return;

    const input = row.querySelector('[data-cart-line-qty]');
    if (!input) return;

    const { itemKey } = row.dataset;
    const currentQty = Number.parseInt(input.value, 10);
    if (Number.isNaN(currentQty)) return;

    // Removal is never debounced — a pending quantity must not land after it. It
    // rides in the same snapshot as a zero rather than cancelling the window, so a
    // burst on a different line is carried along instead of being thrown away.
    if (minusBtn && currentQty === this.MIN) {
      window.CartOptimistic?.markLeaving(row);
      this.setRowQuantity(state, row, 0);
      this.flush(state, onFlush, control);
      return;
    }

    const nextQty = currentQty + (plusBtn ? 1 : -1);

    if (nextQty > this.MAX) {
      onMax?.(itemKey, control, this.MAX);
      return;
    }

    // Unreachable — minus at MIN removes the line — but kept as a guard.
    if (nextQty < this.MIN) return;

    // The input is the local echo, so the next click in a burst reads from it. The
    // bare assignment is the fallback: if the prediction layer failed to load, the
    // stepper must still clamp and mutate rather than break.
    if (window.CartOptimistic) window.CartOptimistic.paintLine(row, nextQty);
    else input.value = nextQty;

    // Last write wins per line: the snapshot carries final quantities, not deltas.
    this.setRowQuantity(state, row, nextQty);

    clearTimeout(state.timer);
    state.timer = setTimeout(() => this.flush(state, onFlush, control), this.DEBOUNCE_MS);
  },

  // The other lines cart-line-group folded into this row (a discount split), as [{ key, quantity }].
  mergedLines(row) {
    return (row?.dataset.mergedLines ?? '')
      .split(',')
      .filter(Boolean)
      .map((entry) => {
        const [key, quantity] = entry.split('=');
        return { key, quantity: Number.parseInt(quantity, 10) || 0 };
      });
  },

  // A merged row's total is spread over its lines: increases land on the row's own line, decreases
  // drain the merged lines first so the row keeps its key. `rows` records the row-level total.
  setRowQuantity(state, row, quantity) {
    const { itemKey, lineOwnQuantity } = row.dataset;
    const merged = this.mergedLines(row);
    state.rows.set(itemKey, quantity);
    if (!merged.length) {
      state.pending.set(itemKey, quantity);
      return;
    }

    const ownQuantity = Number.parseInt(lineOwnQuantity, 10) || 0;
    let excess = ownQuantity + merged.reduce((sum, line) => sum + line.quantity, 0) - quantity;
    for (const line of merged) {
      const dropped = Math.min(line.quantity, Math.max(excess, 0));
      state.pending.set(line.key, line.quantity - dropped);
      excess -= dropped;
    }
    state.pending.set(itemKey, Math.max(ownQuantity - excess, 0));
  },

  // `control` is the last-clicked stepper. It is the error anchor for a single-line
  // snapshot and nothing more — a snapshot spanning several lines has no one control
  // that can speak for it. Public so a host can drain the window early: until this
  // runs the burst has not reached CartMutations, so nothing else can see it pending.
  flush(state, onFlush, control = null) {
    clearTimeout(state.timer);
    state.timer = null;
    if (!state.pending.size) return;

    // `rows` lets a host treat one merged row as the single line the customer touched.
    const snapshot = new Map(state.pending);
    snapshot.rows = new Map(state.rows);
    state.pending.clear();
    state.rows.clear();
    onFlush(snapshot, control);
  },

  clearPending(state) {
    clearTimeout(state.timer);
    state.timer = null;
    state.pending.clear();
    state.rows.clear();
  },
};

window.CartQuantity = CartQuantity;
